/**
 * AI Inline Rail
 * Rail lateral esquerda com ícones de IA que segue o cursor
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FEATURES } from '@/config/features';
import type { ImportantData } from '@/store/aiDataStore';
import { useAIDocDataStore } from '@/store/aiDataStore';
import DataPreviewPanel from './DataPreviewPanel';
import ToneAdjustmentPanel from './ToneAdjustmentPanel';

interface AIInlineRailProps {
  visible: boolean;
  selectedText: string;
  isProcessingOCR?: boolean;
  isProcessingTone?: boolean;
  onSelectCommand: (command: { id: string; label: string }) => void;
  onClose: () => void;
  candidates?: string[];
  importantData?: ImportantData;
  onPickCandidate?: (candidate: string) => void;
  showCandidates?: boolean;
  isUnknownType?: boolean;
  onManualInput?: ({ prompt, range }: { prompt: string; range: any }) => void;
  documentId?: string; // For accessing extracted data
  onAdjustTone?: (selectedText: string, options: { formality: string; documentType: string; tone: string; }) => void;
  onLoadKBData?: () => Promise<void>; // ✅ NOVO: Carregar dados da KB
  casoId?: string; // ✅ NOVO: ID do caso para buscar KB
}

// Singleton global para marcador de caret inofensivo
let CARET_PROBE: HTMLSpanElement | null = null;
function getProbe() {
  if (!CARET_PROBE) {
    CARET_PROBE = document.createElement('span');
    CARET_PROBE.setAttribute('data-ai-caret-probe', 'true');
    CARET_PROBE.style.cssText = 'pointer-events:none;position:fixed;opacity:0;inset:auto auto 0 0;';
  }
  return CARET_PROBE;
}

function measureCaretRectSafely(range: Range) {
  try {
    console.debug(`[AIInlineRail][${Date.now()}] POSITION: Tentando getClientRects()`);
    
    // Primeiro, tentar usar getClientRects() se disponível
    const rects = range.getClientRects();
    if (rects.length > 0) {
      console.debug(`[AIInlineRail][${Date.now()}] POSITION: source: 'rect', coords: {top: ${rects[0].top}, left: ${rects[0].left}}`);
      return rects[0];
    }
    
    console.debug(`[AIInlineRail][${Date.now()}] POSITION: Fallback para getBoundingClientRect()`);
    // Fallback para getBoundingClientRect()
    const rect = range.getBoundingClientRect();
    if (rect.width > 0 || rect.height > 0) {
      console.debug(`[AIInlineRail][${Date.now()}] POSITION: source: 'bbox', coords: {top: ${rect.top}, left: ${rect.left}}`);
      return rect;
    }
    
    console.debug(`[AIInlineRail][${Date.now()}] POSITION: Usando marcador invisível para linha vazia`);
    // Último resort: usar marcador invisível para linhas vazias
    const marker = document.createElement('span');
    marker.setAttribute('data-ai-caret', '1');
    marker.style.cssText = 'position:relative;display:inline-block;line-height:1';
    marker.textContent = '\u200b';
    
    range.insertNode(marker);
    const markerRect = marker.getBoundingClientRect();
    marker.remove();
    
    if (markerRect.width > 0 || markerRect.height > 0) {
      console.debug(`[AIInlineRail][${Date.now()}] POSITION: source: 'marker', coords: {top: ${markerRect.top}, left: ${markerRect.left}}`);
      return markerRect;
    }
    
    return null;
  } catch (error) {
    console.error(`[AIInlineRail][${Date.now()}] POSITION: Erro ao medir posição do caret:`, error);
    return null;
  }
}

// Comandos da rail (estilo Google Docs - apenas essenciais)
const RAIL_COMMANDS = [
  { id: 'addData', label: 'Add Dados', icon: 'svg', requiresSelection: false },
  { id: 'tone', label: 'Ajustar tom', icon: 'svg', requiresSelection: true },
  { id: 'manualInput', label: 'Gerar Texto', icon: 'svg', requiresSelection: false },
];

export const AIInlineRail: React.FC<AIInlineRailProps> = ({
  visible,
  selectedText,
  isProcessingOCR = false,
  isProcessingTone = false,
  onSelectCommand,
  onClose,
  candidates = [],
  importantData,
  onPickCandidate,
  onLoadKBData, // ✅ NOVO
  casoId, // ✅ NOVO
  showCandidates = false,
  isUnknownType = false,
  onManualInput,
  documentId,
  onAdjustTone,
}) => {
  const railRef = useRef<HTMLDivElement>(null);
  const railContentRef = useRef<HTMLDivElement>(null);
  const [railStyle, setRailStyle] = useState<React.CSSProperties>({ position: 'fixed', left: 0, top: 8, opacity: 0 });
  const [activePanel, setActivePanel] = useState<string | null>(null);
  const [showTonePanel, setShowTonePanel] = useState(false);
  const [manualInputText, setManualInputText] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [showDataPreview, setShowDataPreview] = useState(false);
  const [hoverTimer, setHoverTimer] = useState<NodeJS.Timeout | null>(null);
  const [userClosedPreview, setUserClosedPreview] = useState(false);
  const [isPanelHovered, setIsPanelHovered] = useState(false);
  const [lastValidTop, setLastValidTop] = useState(8);
  const [isProcessingManualInput, setIsProcessingManualInput] = useState(false);
  const savedRangeRef = useRef<Range | null>(null);

  const hasOcrData = useAIDocDataStore(s => s.hasData(documentId || 'current'));

  const hasSelection = selectedText.trim().length > 0;

  // Detectar mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // POSICIONAMENTO ROBUSTO - Correção para linhas vazias
  useEffect(() => {
    const pageEl = document.querySelector('[data-doc-anchor="paper"]') as HTMLElement | null;
    if (!pageEl) return;

    let raf = 0;

    const getCaretRectRobusto = (): DOMRect | null => {
      // GUARDA 1: NUNCA atuar em <input>/<textarea>
      const el = document.activeElement as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA)$/i.test(el.tagName)) return null;
      
      // GUARDA 1.5: 🆕 WHITELIST para inputs específicos (campo de busca, chat, etc.)
      if (el && (
        el.closest('[data-caso-search]') ||
        el.closest('[data-prompt-input]') ||
        el.closest('[data-doc-chat]')
      )) {
        return null;
      }
      
      const sel = getSelection();
      if (!sel || sel.rangeCount === 0) return null;
      const range = sel.getRangeAt(0);
      
      // GUARDA 2: Guardas de escopo no contentEditable
      const editorRoot = document.querySelector('[contenteditable]');
      if (!editorRoot || !editorRoot.contains(range.startContainer)) return null;

      // Usar o sistema singleton seguro
      return measureCaretRectSafely(range);
    };

    const updatePosition = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const caretRect = getCaretRectRobusto();
        if (!caretRect) return;

        const editorRoot = pageEl;
        const editorRect = editorRoot.getBoundingClientRect();
        
        // Calcular posição ABSOLUTA na viewport (para position: fixed)
        const railLeftInViewport = editorRect.right + 8; // 8px à direita do editor
        const caretTopInViewport = caretRect.top;
        
        // altura real da rail (evita erro em zoom/telas diferentes)
        const railH = railRef.current?.getBoundingClientRect().height ?? 40;
        
        // Verificar se está dentro do editor
        const isInside = caretRect.top >= editorRect.top - 1 && caretRect.bottom <= editorRect.bottom + 1;
        
        if (!isInside) {
          // Fora do editor -> usar última posição válida
          setRailStyle(prev => ({ ...prev, top: lastValidTop, left: railLeftInViewport }));
          return;
        }
        
        // Limites verticais (viewport absoluta)
        const minTop = editorRect.top + 8;
        const maxTop = editorRect.bottom - railH - 8;
        const nextTop = Math.max(minTop, Math.min(caretTopInViewport, maxTop));
        
        // Salvar posição válida
        setLastValidTop(nextTop);
        setRailStyle(prev => ({ 
          ...prev, 
          position: 'fixed',
          left: railLeftInViewport,
          top: Math.round(nextTop),
          opacity: 1  // Tornar visível após primeiro cálculo
        }));
      });
    };

    // listeners (cobrem todos os cenários)
    const onSel = (event: Event) => {
      // Só move rail se mudança de seleção for no documento, não em componentes AI (CORREÇÃO 4)
      const activeElement = document.activeElement;  
      const target = event.target as Element;
      
      const isAIComponent = activeElement?.closest('[data-component="ai-inline-rail"], [data-component="data-preview-panel"], [data-component="tone-adjustment-panel"], [data-radix-popper-content-wrapper]') ||
                           (target && typeof target.closest === 'function' ? target.closest('[data-component="ai-inline-rail"], [data-component="data-preview-panel"], [data-component="tone-adjustment-panel"], [data-radix-popper-content-wrapper]') : null);
      
      if (!isAIComponent) {
        updatePosition();
      }
    };
    const onKey = updatePosition;
    const onScroll = updatePosition;
    const onResize = updatePosition;
    const onComp = updatePosition; // IME (digitação por composição)

    // CORREÇÃO: Listener específico para cliques no editor
    const onEditorClick = (event: MouseEvent) => {
      const editor = document.querySelector('[contenteditable="true"]');
      if (editor && editor.contains(event.target as Node)) {
        setTimeout(updatePosition, 10); // Pequeno delay para a seleção se estabelecer
      }
    };

    document.addEventListener('selectionchange', onSel);
    document.addEventListener('click', onEditorClick); // NOVO: escuta cliques no editor
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('keyup', onKey, true);
    document.addEventListener('compositionstart', onComp, true);
    document.addEventListener('compositionupdate', onComp, true);
    document.addEventListener('compositionend', onComp, true);

    // se o papel/viewport rolam, escute ambos
    const scrollTargets = [document, pageEl, pageEl.parentElement].filter(Boolean) as EventTarget[];
    scrollTargets.forEach(t => t.addEventListener('scroll', onScroll, { passive: true }));
    window.addEventListener('resize', onResize);

    // posiciona imediatamente ao montar/focar
    updatePosition();

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('selectionchange', onSel);
      document.removeEventListener('click', onEditorClick); // NOVO: remove listener de click
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('keyup', onKey, true);
      document.removeEventListener('compositionstart', onComp, true);
      document.removeEventListener('compositionupdate', onComp, true);
      document.removeEventListener('compositionend', onComp, true);
      scrollTargets.forEach(t => t.removeEventListener('scroll', onScroll as any));
      window.removeEventListener('resize', onResize);
    };
  }, [visible]);

  // Fechar painel ao clicar fora e Esc
  useEffect(() => {
    if (!activePanel) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const isInsideRail = railRef.current && railRef.current.contains(target);
      const isInsidePanel = target.closest('[data-component="tone-adjustment-panel"]');
      const isInsideRadixDropdown = target.closest('[data-radix-popper-content-wrapper]');
      
      if (!isInsideRail && !isInsidePanel && !isInsideRadixDropdown) {
        setActivePanel(null);
        setShowTonePanel(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActivePanel(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [activePanel]);

  if (!visible) {
    return null;
  }

  const handleCommandClick = (command: typeof RAIL_COMMANDS[0]) => {
    console.debug(`[AIInlineRail][${Date.now()}] CLICK: Botão clicado:`, command.id);
    
    // CORREÇÃO: Prevenir perda de foco ao clicar na rail
    const activeElement = document.activeElement;
    if (activeElement && activeElement.getAttribute('contenteditable') === 'true') {
      console.debug(`[AIInlineRail][${Date.now()}] CLICK: Foco preservado no editor`);
    }
    
    if (command.requiresSelection && !hasSelection) {
      console.warn(`[AIInlineRail][${Date.now()}] CLICK: ❌ Comando requer seleção mas nenhuma foi encontrada`);
      return;
    }
    
    if (command.id === 'manualInput') {
      console.debug(`[AIInlineRail][${Date.now()}] CLICK: Capturando seleção para Gerar Texto...`);
      
      // Capturar e serializar range antes de abrir painel (correção definitiva)
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        // Serializar posições ao invés de armazenar objeto Range
        savedRangeRef.current = {
          startContainer: range.startContainer,
          startOffset: range.startOffset,
          endContainer: range.endContainer,
          endOffset: range.endOffset,
          collapsed: range.collapsed
        } as any;
        console.debug(`[AIInlineRail][${Date.now()}] SNAPSHOT: Seleção serializada:`, {
          collapsed: range.collapsed,
          text: range.toString().substring(0, 30)
        });
      } else {
        console.debug(`[AIInlineRail][${Date.now()}] SNAPSHOT: Nenhuma seleção ativa - será inserido no caret`);
        savedRangeRef.current = null;
      }
      
      setActivePanel('manualInput');
      setShowDataPreview(false);
      setShowTonePanel(false);
    } else if (command.id === 'addData') {
      // Show menu if: has OCR data OR has KB access
      if (hasOcrData || (casoId && onLoadKBData)) {
        setActivePanel('addDataMenu');
        setShowDataPreview(false);
        setShowTonePanel(false);
      } else {
        // Reset user closed state on new upload action
        setUserClosedPreview(false);
        // Normal upload flow if no data
        onSelectCommand(command);
        setActivePanel(null);
        setShowDataPreview(false);
        setShowTonePanel(false);
      }
    } else if (command.id === 'tone') {
      setActivePanel('tone');
      setShowTonePanel(true);
      setShowDataPreview(false);
    } else {
      onSelectCommand(command);
      setActivePanel(null);
      setShowDataPreview(false);
      setShowTonePanel(false);
    }
  };

  const handleAddDataHover = (entering: boolean) => {
    if (userClosedPreview) return;

    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }

    if (entering && !activePanel) {
      // Só mostra em hover se usuário não fechou manualmente
      setShowDataPreview(true);
    } else if (!isPanelHovered) {
      // Só fecha se o mouse não estiver no painel (CORREÇÃO 2)
      const timer = setTimeout(() => {
        if (!isPanelHovered) {
          setShowDataPreview(false);
        }
      }, 300);
      setHoverTimer(timer);
    }
  };

  // Auto-abertura do DataPreviewPanel após upload (CORREÇÃO 1)
  useEffect(() => {
    if (hasOcrData && !showDataPreview && !activePanel && !userClosedPreview) {
      console.log('🔄 Auto-opening preview - data available');
      setShowDataPreview(true);
    }
  }, [hasOcrData, showDataPreview, activePanel, userClosedPreview]);

  // Auto-close tone panel when processing completes (but only if user didn't manually close)
  useEffect(() => {
    if (!isProcessingTone && activePanel === 'tone' && showTonePanel) {
      // Keep the panel open after processing completes - let user close manually
      // setActivePanel(null);
      // setShowTonePanel(false);
    }
  }, [isProcessingTone, activePanel, showTonePanel]);

  const handleDataInsert = (value: string) => {
    if (onPickCandidate) {
      onPickCandidate(value);
    }
    setUserClosedPreview(true); // Usuário interagiu - não abre mais automaticamente
  };

  const handleManualInputSubmit = async () => {
    console.log('[AIInlineRail][SUBMIT] Iniciando submissão manual:', new Date().toISOString());
    
    if (!manualInputText.trim() || isProcessingManualInput) {
      console.warn('[AIInlineRail][SUBMIT] ❌ Texto vazio ou já processando');
      return;
    }
    
    console.log('[AIInlineRail][SUBMIT] Prompt:', manualInputText.substring(0, 50));
    console.log('[AIInlineRail][SUBMIT] Range salvo:', !!savedRangeRef.current);
    
    setIsProcessingManualInput(true);
    try {
      // Usar os dados serializados do range (correção definitiva)
      const rangeData = savedRangeRef.current;
      
      if (onManualInput) {
        await onManualInput({ prompt: manualInputText.trim(), range: rangeData });
        console.log('[AIInlineRail][SUBMIT] ✅ Submissão concluída com sucesso');
      }
      setManualInputText('');
      setActivePanel(null);
      savedRangeRef.current = null; // Limpar após uso
    } catch (error) {
      console.error('[AIInlineRail][SUBMIT] ❌ Erro na submissão:', error);
    } finally {
      setIsProcessingManualInput(false);
    }
  };

  const renderPanel = () => {
    if (!activePanel || activePanel === 'tone') return null;

    const panelContent = (
      <div className="ai-rail-panel bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 max-h-[80vh] overflow-y-auto">
        {/* Manual Input Panel - CORREÇÃO 2: Largura idêntica ao DataPreviewPanel */}
        {activePanel === 'manualInput' && (
          <div className="space-y-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              📝 Gerar Texto
            </div>
            <textarea
              data-prompt-input="true"
              value={manualInputText}
              onChange={(e) => setManualInputText(e.target.value)}
              placeholder="Digite seu prompt para gerar texto..."
              className="ai-manual-textarea p-2 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-700/50 w-full"
              rows={6}
              style={{ minHeight: '120px' }}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  console.debug(`[AIInlineRail][${Date.now()}] CLICK: preventDefault aplicado no botão Gerar`);
                }}
                onClick={handleManualInputSubmit}
                disabled={!manualInputText.trim() || isProcessingManualInput}
                className="px-3 py-1.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none rounded-md transition-colors flex items-center gap-1"
              >
                {isProcessingManualInput && <span className="animate-spin">⏳</span>}
                {isProcessingManualInput ? 'Gerando...' : 'Gerar'}
              </button>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  console.debug(`[AIInlineRail][${Date.now()}] CLICK: preventDefault aplicado no botão Cancelar`);
                }}
                onClick={() => setActivePanel(null)}
                className="px-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Add Data Menu */}
        {activePanel === 'addDataMenu' && (
          <div className="space-y-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              🖼️ Adicionar Dados
            </div>
            <div className="space-y-2">
              {/* ✅ NOVO: Buscar da Base de Conhecimento */}
              {(() => {
                console.log('[AIInlineRail][DEBUG] Add Data Menu:', { 
                  casoId, 
                  hasOnLoadKBData: !!onLoadKBData,
                  shouldShowButton: !!(casoId && onLoadKBData)
                });
                return casoId && onLoadKBData ? (
                  <button
                    onClick={async () => {
                      console.log('[AIInlineRail][DEBUG] Botão KB clicado!');
                      setActivePanel(null);
                      await onLoadKBData();
                      setShowDataPreview(true);
                    }}
                    className="w-full text-left px-3 py-2 text-sm bg-accent/10 hover:bg-accent/20 text-accent-foreground rounded-md transition-colors flex items-center gap-2"
                  >
                    <span>📚</span>
                    <span>Buscar da Base de Conhecimento</span>
                  </button>
                ) : null;
              })()}
              
              <button
                onClick={() => {
                  setShowDataPreview(true);
                  setActivePanel(null);
                }}
                className="w-full text-left px-3 py-2 text-sm bg-primary/10 hover:bg-primary/20 text-primary rounded-md transition-colors"
              >
                📄 Ver dados extraídos
              </button>
              <button
                  onClick={() => {
                    setUserClosedPreview(false); // Reset estado para novo upload
                    onSelectCommand({ id: 'addData', label: 'Add Dados' });
                    setActivePanel(null);
                    setShowDataPreview(false);
                  }}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
              >
                📤 Novo upload
              </button>
            </div>
          </div>
        )}


        {/* Candidatos e Dados Importantes (reutilizar do tooltip original) */}
        {activePanel === 'addData' && (
          <div className="space-y-3">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              🖼️ Adicionar Dados
            </div>
            
            {/* Lista de candidatos específicos */}
            {showCandidates && candidates.length > 0 && !isUnknownType && (
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Sugestões do tipo selecionado ({candidates.length})
                </div>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {candidates.slice(0, 5).map((candidate, index) => (
                    <button
                      key={index}
                      onClick={() => onPickCandidate?.(candidate)}
                      className="w-full text-left px-2 py-1.5 text-xs bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-800 dark:text-blue-200 rounded border border-blue-200 dark:border-blue-800 transition-colors"
                      title={`Inserir: ${candidate}`}
                    >
                      <span className="truncate block">{candidate}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Dados Importantes do Documento */}
            {importantData && (isUnknownType || Object.values(importantData).some(arr => arr.length > 0)) && (
              <div className="space-y-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Dados Importantes do Documento
                </div>
                
                {/* Nomes */}
                {importantData.names.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      👤 Nomes ({importantData.names.length})
                    </div>
                    <div className="space-y-1">
                      {importantData.names.slice(0, 3).map((name, index) => (
                        <button
                          key={index}
                          onClick={() => onPickCandidate?.(name)}
                          className="w-full text-left px-2 py-1 text-xs bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 text-green-800 dark:text-green-200 rounded border border-green-200 dark:border-green-800 transition-colors"
                          title={`Inserir nome: ${name}`}
                        >
                          <span className="truncate block">{name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documentos */}
                {importantData.documents.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      📄 Documentos ({importantData.documents.length})
                    </div>
                    <div className="space-y-1">
                      {importantData.documents.slice(0, 3).map((doc, index) => (
                        <button
                          key={index}
                          onClick={() => onPickCandidate?.(doc)}
                          className="w-full text-left px-2 py-1 text-xs bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/40 text-purple-800 dark:text-purple-200 rounded border border-purple-200 dark:border-purple-800 transition-colors"
                          title={`Inserir documento: ${doc}`}
                        >
                          <span className="truncate block">{doc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contatos */}
                {importantData.contacts.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                      📞 Contatos ({importantData.contacts.length})
                    </div>
                    <div className="space-y-1">
                      {importantData.contacts.slice(0, 3).map((contact, index) => (
                        <button
                          key={index}
                          onClick={() => onPickCandidate?.(contact)}
                          className="w-full text-left px-2 py-1 text-xs bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/40 text-orange-800 dark:text-orange-200 rounded border border-orange-200 dark:border-orange-800 transition-colors"
                          title={`Inserir contato: ${contact}`}
                        >
                          <span className="truncate block">{contact}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );

    // Mobile: Sheet lateral esquerda
    if (isMobile) {
      return createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex">
          <div className="w-[min(90vw,480px)] bg-white dark:bg-gray-800 h-full overflow-y-auto">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  IA Assistente
                </h3>
                <button
                  onClick={() => setActivePanel(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
              {panelContent}
            </div>
          </div>
          <div className="flex-1" onClick={() => setActivePanel(null)} />
        </div>,
        document.body
      );
    }

    // Desktop: Painel para a esquerda
    return (
      <div
        className="absolute z-[10001] transition-opacity duration-200"
        style={{
          right: '100%',
          top: 0,
          marginRight: '8px',
        }}
      >
        {panelContent}
      </div>
    );
  };

  // Determinar se deve mostrar backdrop
  const shouldShowBackdrop = activePanel !== null || showDataPreview;

  // Portal root: sempre document.body para sair do stacking context das colunas
  const portalRoot = typeof window !== 'undefined' ? document.body : null;
  
  if (!portalRoot) return null;

  return createPortal(
    <>
      {/* Backdrop blur: cobre tudo exceto RAIL (z-10000) */}
      {shouldShowBackdrop && (
        <div
          className="fixed inset-0 bg-black/5 backdrop-blur-[2px] z-[10000]"
          style={{ pointerEvents: 'none' }}
          aria-hidden="true"
        />
      )}
      
      {/* Camada global do RAIL: acima do backdrop (z-10001) */}
      <div className="fixed inset-0 z-[10001] pointer-events-none">
        {/* Fix para dropdowns Radix ficarem acima do blur (z-10002) */}
        <style>{`
          [data-radix-popper-content-wrapper],
          [data-radix-portal] {
            z-index: 10002 !important;
          }
        `}</style>

        {/* Container do RAIL: pode clicar (pointer-events-auto) */}
        <div
          ref={railRef}
          className="pointer-events-auto transition-all duration-200"
          style={{
            position: 'fixed',
            left: railStyle.left,
            top: railStyle.top,
            opacity: railStyle.opacity ?? 1,
          }}
        >
          {/* Rail de ícones - Estilo Google Docs */}
          <div ref={railContentRef} className="flex flex-col items-center gap-1 bg-white dark:bg-gray-800 rounded-full p-1 shadow-lg border border-gray-200 dark:border-gray-700" role="toolbar" aria-label="IA Assistente">
            {RAIL_COMMANDS.map((command) => {
              const isDisabled = command.requiresSelection && !hasSelection;
              const hasExtractedData = command.id === 'addData' ? hasOcrData : false;
              
              return (
                <div key={command.id} className="relative">
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      console.debug(`[AIInlineRail][${Date.now()}] CLICK: preventDefault aplicado no botão ${command.id}`);
                    }}
                    onClick={() => handleCommandClick(command)}
                    onMouseEnter={() => command.id === 'addData' && handleAddDataHover(true)}
                    onMouseLeave={() => command.id === 'addData' && handleAddDataHover(false)}
                    disabled={isDisabled}
                    className={`
                      h-8 w-8 inline-flex items-center justify-center rounded-full text-xs transition-all duration-200 relative
                      ${isDisabled 
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed' 
                        : 'bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 hover:scale-105'
                      }
                      ${activePanel === command.id && command.id !== 'tone' && command.id !== 'manualInput' ? 'bg-primary text-primary-foreground' : ''}
                      ${activePanel === 'manualInput' && command.id === 'manualInput' ? 'bg-[#cbcbcb] text-gray-700 dark:text-gray-300' : ''}
                      ${showDataPreview && command.id === 'addData' ? 'bg-[#cbcbcb] text-gray-700 dark:text-gray-300' : ''}
                      ${showTonePanel && command.id === 'tone' ? 'bg-[#cbcbcb] text-gray-700 dark:text-gray-300' : ''}
                    `}
                    title={command.label}
                    aria-label={command.label}
                  >
                    {/* Loading spinner para Add Dados */}
                    {command.id === 'addData' && isProcessingOCR ? (
                      <div className="animate-spin text-sm">⏳</div>
                    ) : command.id === 'addData' ? (
                      <img src="/add_dados.svg" alt="Add Dados" className="w-6 h-6" />
                    ) : command.id === 'tone' && isProcessingTone ? (
                      <div className="animate-spin text-sm">⏳</div>
                    ) : command.id === 'tone' ? (
                      <img src="/ajustar_t.svg" alt="Ajustar Tom" className="w-6 h-6" />
                    ) : command.id === 'manualInput' ? (
                      <img src="/gerar_txt.svg" alt="Gerar Texto" className="w-6 h-6" />
                    ) : (
                      <span>{command.icon}</span>
                    )}
                    {hasExtractedData && !isProcessingOCR && (
                      <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full border border-white dark:border-gray-800"></div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Painel expansível */}
          {renderPanel()}
          
          {/* Data Preview Panel */}
          <DataPreviewPanel
            open={showDataPreview}
            onClose={() => {
              setShowDataPreview(false);
              setUserClosedPreview(true); // Lembrar que usuário fechou manualmente
            }}
            data={null} // DataPreviewPanel has internal fallback using getAllImportantData
            onInsert={handleDataInsert}
            documentId={documentId}
            onMouseEnter={() => {
              setIsPanelHovered(true);
              if (hoverTimer) {
                clearTimeout(hoverTimer);
                setHoverTimer(null);
              }
            }}
            onMouseLeave={() => {
              setIsPanelHovered(false); 
              if (showDataPreview) {
                const timer = setTimeout(() => {
                  if (!isPanelHovered) {
                    setShowDataPreview(false);
                  }
                }, 300);
                setHoverTimer(timer);
              }
            }}
          />

          {/* Tone Adjustment Panel - rendered directly without wrapper */}
          {activePanel === 'tone' && (
            <ToneAdjustmentPanel
              visible={true}
              selectedText={selectedText}
              isProcessing={isProcessingTone}
              onClose={() => {
                setShowTonePanel(false);
                setActivePanel(null);
              }}
              onAdjustTone={async (options) => {
                if (selectedText && onAdjustTone) {
                  await onAdjustTone(selectedText, options);
                }
              }}
            />
          )}
        </div>
      </div>
    </>,
    portalRoot
  );
};

export default AIInlineRail;